import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ViewPage } from '../components/ViewPage';
import { StatCard } from '../components/StatCard';
import GlobalEmptyState from '../components/GlobalEmptyState';
import { GlobalButton } from '../components/GlobalButton';
import { useOrg } from '../context/OrgContext';
import { useUser } from '../context/UserContext';
import { useLocale } from '../context/DynamicLocaleProvider';
import { useOrgApplications, useOrgCases, useOrgCustomers, useOrgDocuments } from '../hooks/useOrgData';
import { formatCents } from '../utils/money';
import { isOrgAdmin } from '../utils/roles';

const OPEN_STATUSES = new Set([
  'draft',
  'ready',
  'sent',
  'opened',
  'in_progress',
  'submitted',
  'under_review',
]);

export function HomeView() {
  const { t } = useLocale();
  const { org } = useOrg();
  const { role } = useUser();
  const navigate = useNavigate();
  const { customers, loading: customersLoading } = useOrgCustomers();
  const { documents, loading: docsLoading } = useOrgDocuments();
  const { cases, loading: casesLoading } = useOrgCases();
  const { applications } = useOrgApplications();

  const stats = useMemo(() => {
    const openCases = cases.filter((c) => OPEN_STATUSES.has(c.status)).length;
    const submitted = cases.filter((c) =>
      ['submitted', 'under_review', 'approved', 'rejected'].includes(c.status)
    ).length;
    const approvedVolume = applications
      .filter((a) => a.status === 'approved')
      .reduce((s, a) => s + a.financedAmountCents, 0);
    const needsReview = documents.filter((d) => d.extractionStatus === 'needs_review').length;
    const now = Date.now();
    const week = 7 * 24 * 60 * 60 * 1000;
    const expiring = cases.filter((c) => {
      if (!c.expiresAt || !OPEN_STATUSES.has(c.status)) return false;
      const ms = typeof c.expiresAt.toMillis === 'function' ? c.expiresAt.toMillis() : 0;
      return ms > now && ms - now < week;
    }).length;
    return { openCases, submitted, approvedVolume, needsReview, expiring };
  }, [cases, applications, documents]);

  const loading = customersLoading || docsLoading || casesLoading;
  const empty = !loading && customers.length === 0 && cases.length === 0;

  return (
    <ViewPage
      title={t('home_title')}
      actions={
        isOrgAdmin(role) ? (
          <>
            <GlobalButton type="button" variant="secondary" onClick={() => navigate('/documents')}>
              {t('home_cta_document')}
            </GlobalButton>
            <GlobalButton type="button" onClick={() => navigate('/cases/new')}>
              {t('home_cta_case')}
            </GlobalButton>
          </>
        ) : null
      }
    >
      <div className="stat-grid" style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', marginBottom: 24 }}>
        <StatCard label={t('home_cases_open')} value={String(stats.openCases)} />
        <StatCard label={t('home_cases_submitted')} value={String(stats.submitted)} />
        <StatCard label={t('home_volume')} value={formatCents(stats.approvedVolume)} />
        <StatCard label={t('home_customers')} value={String(customers.length)} />
        <StatCard label={t('home_docs_review')} value={String(stats.needsReview)} />
        <StatCard label={t('home_expiring')} value={String(stats.expiring)} />
      </div>
      {empty ? (
        <GlobalEmptyState title={t('home_empty')} description={org?.legalName || org?.name || ''} />
      ) : null}
    </ViewPage>
  );
}
